import { useEffect, useState, useMemo, useCallback } from 'react';
import { listProjects, listProjectActuals, createProjectActual, deleteProjectActual } from '../../api/projects';
import type { ProjectActual } from '../../api/projects';
import type { GrandProjet } from '../../types/estimation';
import { toast } from '../../stores/uiStore';
import { fmt, fmtCHF, fmtPct } from '../../utils/format';
import { computeN1, FALLBACK_RATIOS, FALLBACK_BENCHMARKS } from '../../utils/estimation';
import { useTranslation } from 'react-i18next';
import { InfoTip } from '../shared/InfoTip';
import styles from './ActualsPage.module.css';

interface FormData {
  period: string;
  logements_delivered: string;
  cost_spent: string;
  emplois_created: string;
  sbp_built: string;
  inhabitants_moved: string;
  notes: string;
}

const emptyForm: FormData = {
  period: '', logements_delivered: '', cost_spent: '', emplois_created: '',
  sbp_built: '', inhabitants_moved: '', notes: '',
};

interface ActualsPageProps {
  /** When provided (e.g. from project detail tab), skip the project selector */
  projectId?: string;
}

export function ActualsPage({ projectId: propProjectId }: ActualsPageProps) {
  const { t } = useTranslation(['actuals', 'common']);
  const [projects, setProjects] = useState<GrandProjet[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>(propProjectId ?? '');
  const [actuals, setActuals] = useState<ProjectActual[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormData>({ ...emptyForm });

  const isEmbedded = !!propProjectId;

  // Load custom projects (standalone: all projects; embedded: just the one)
  useEffect(() => {
    if (isEmbedded) {
      // In embedded mode, load the single project for planned values
      if (propProjectId) {
        import('../../api/projects').then(({ getProject }) =>
          getProject(propProjectId).then((p) => setProjects([p]))
        ).catch(() => {}).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
      return;
    }
    listProjects({ is_custom: true }).then((data) => {
      setProjects(data);
      if (data.length > 0 && !selectedProjectId) {
        setSelectedProjectId(data[0]!.id);
      }
      setLoading(false);
    }).catch(() => {
      toast.error(t('actuals:failedToLoadProjects'));
      setLoading(false);
    });
  }, [isEmbedded, propProjectId]);

  // Load actuals when project changes
  const loadActuals = useCallback(async () => {
    if (!selectedProjectId) return;
    try {
      const data = await listProjectActuals(selectedProjectId);
      setActuals(data);
    } catch {
      toast.error(t('actuals:failedToLoadActuals'));
    }
  }, [selectedProjectId]);

  useEffect(() => { loadActuals(); }, [loadActuals]);

  const selectedProject = projects.find((p) => p.id === selectedProjectId);

  // Compute planned values from the project
  const planned = useMemo(() => {
    if (!selectedProject) return null;
    const ha = selectedProject.ha_total ?? 0;
    const haZi = selectedProject.ha_industriel ?? 0;
    const result = computeN1(Number(ha), Number(haZi), FALLBACK_RATIOS, FALLBACK_BENCHMARKS);
    return {
      logements: selectedProject.logements || result.logements,
      cost: result.cost_total,
      emplois: selectedProject.emplois || result.emplois,
      sbp: selectedProject.sbp_total || result.sbp_total,
      inhabitants: result.inhabitants,
    };
  }, [selectedProject]);

  // Compute cumulative actuals
  const cumActuals = useMemo(() => {
    const sum = { logements: 0, cost: 0, emplois: 0, sbp: 0, inhabitants: 0 };
    for (const a of actuals) {
      sum.logements += a.logements_delivered ?? 0;
      sum.cost += a.cost_spent ?? 0;
      sum.emplois += a.emplois_created ?? 0;
      sum.sbp += a.sbp_built ?? 0;
      sum.inhabitants += a.inhabitants_moved ?? 0;
    }
    return sum;
  }, [actuals]);

  // Variance metrics
  const metrics = useMemo(() => {
    if (!planned) return [];
    const items = [
      { key: 'logements', labelKey: 'metricLogements', planned: planned.logements, actual: cumActuals.logements, color: '#6366f1', format: fmt },
      { key: 'cost', labelKey: 'metricCost', planned: planned.cost, actual: cumActuals.cost, color: '#e74c3c', format: fmtCHF },
      { key: 'emplois', labelKey: 'metricEmplois', planned: planned.emplois, actual: cumActuals.emplois, color: '#f39c12', format: fmt },
      { key: 'sbp', labelKey: 'metricSBP', planned: planned.sbp, actual: cumActuals.sbp, color: '#9b59b6', format: fmt },
      { key: 'inhabitants', labelKey: 'metricInhabitants', planned: planned.inhabitants, actual: cumActuals.inhabitants, color: '#22d3ee', format: fmt },
    ];
    return items.map((m) => ({
      ...m,
      label: t(`actuals:${m.labelKey}`),
      variance: m.planned !== 0 ? ((m.actual - m.planned) / Math.abs(m.planned)) * 100 : 0,
      completionPct: m.planned !== 0 ? (m.actual / Math.abs(m.planned)) * 100 : 0,
    }));
  }, [planned, cumActuals, t]);

  // Early warning alerts
  const alerts = useMemo(() => {
    const list: { type: 'warning' | 'danger' | 'success'; message: string }[] = [];
    for (const m of metrics) {
      if (m.key === 'cost' && m.variance > 15) {
        list.push({ type: 'danger', message: t('actuals:alertCostOverrun', { pct: fmtPct(m.variance, 1) }) });
      } else if (m.key === 'cost' && m.variance > 5) {
        list.push({ type: 'warning', message: t('actuals:alertCostTrending', { pct: fmtPct(m.variance, 1) }) });
      }
      if (m.key === 'logements' && m.completionPct > 0 && m.variance < -20) {
        list.push({ type: 'warning', message: t('actuals:alertLogementsBehind', { pct: fmtPct(m.completionPct, 0) }) });
      }
    }
    if (actuals.length === 0) {
      list.push({ type: 'success', message: t('actuals:noActualsAddFirst') });
    }
    return list;
  }, [metrics, actuals, t]);

  async function handleSubmit() {
    if (!selectedProjectId || !form.period.trim()) {
      toast.error(t('actuals:periodRequired'));
      return;
    }
    try {
      await createProjectActual(selectedProjectId, {
        period: form.period.trim(),
        logements_delivered: form.logements_delivered ? Number(form.logements_delivered) : null,
        cost_spent: form.cost_spent ? Number(form.cost_spent) : null,
        emplois_created: form.emplois_created ? Number(form.emplois_created) : null,
        sbp_built: form.sbp_built ? Number(form.sbp_built) : null,
        inhabitants_moved: form.inhabitants_moved ? Number(form.inhabitants_moved) : null,
        notes: form.notes || null,
      });
      toast.success(t('actuals:actualRecorded'));
      setForm({ ...emptyForm });
      setShowForm(false);
      await loadActuals();
    } catch {
      toast.error(t('actuals:failedToSave'));
    }
  }

  async function handleDelete(actualId: string) {
    try {
      await deleteProjectActual(selectedProjectId, actualId);
      toast.success(t('actuals:actualDeleted'));
      await loadActuals();
    } catch {
      toast.error(t('actuals:failedToDelete'));
    }
  }

  if (loading) return <div className={styles.loading}>{t('common:loading')}</div>;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h2>{t('actuals:title')}<InfoTip text={t('actuals:tooltips.title')} /></h2>
          <div className={styles.headerSub}>
            {t('actuals:subtitle')}
          </div>
        </div>
        <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => setShowForm(!showForm)}>
          {showForm ? t('common:cancel') : t('actuals:recordActual')}
        </button>
      </div>

      {/* Project selector (hidden when embedded in project detail) */}
      {!isEmbedded && (
        <div className={styles.projectSelect}>
          <label>{t('common:selectProject')}:</label>
          <select
            value={selectedProjectId}
            onChange={(e) => setSelectedProjectId(e.target.value)}
          >
            {projects.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Add form */}
      {showForm && (
        <div className={styles.addForm}>
          <div className={styles.formTitle}>{t('actuals:recordActualData')}</div>
          <div className={styles.formGrid}>
            <div className={styles.formField}>
              <label>{t('actuals:period')}</label>
              <input
                type="text"
                placeholder={t('actuals:periodPlaceholder')}
                value={form.period}
                onChange={(e) => setForm({ ...form, period: e.target.value })}
              />
            </div>
            <div className={styles.formField}>
              <label>{t('actuals:logementsDelivered')}</label>
              <input
                type="number"
                value={form.logements_delivered}
                onChange={(e) => setForm({ ...form, logements_delivered: e.target.value })}
              />
            </div>
            <div className={styles.formField}>
              <label>{t('actuals:costSpent')}</label>
              <input
                type="number"
                value={form.cost_spent}
                onChange={(e) => setForm({ ...form, cost_spent: e.target.value })}
              />
            </div>
            <div className={styles.formField}>
              <label>{t('actuals:emploisCreated')}</label>
              <input
                type="number"
                value={form.emplois_created}
                onChange={(e) => setForm({ ...form, emplois_created: e.target.value })}
              />
            </div>
            <div className={styles.formField}>
              <label>{t('actuals:sbpBuilt')}</label>
              <input
                type="number"
                value={form.sbp_built}
                onChange={(e) => setForm({ ...form, sbp_built: e.target.value })}
              />
            </div>
            <div className={styles.formField}>
              <label>{t('actuals:inhabitantsMoved')}</label>
              <input
                type="number"
                value={form.inhabitants_moved}
                onChange={(e) => setForm({ ...form, inhabitants_moved: e.target.value })}
              />
            </div>
          </div>
          <div className={styles.formField} style={{ marginBottom: 12 }}>
            <label>{t('actuals:notes')}</label>
            <textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>
          <div className={styles.formActions}>
            <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={handleSubmit}>
              {t('actuals:saveActual')}
            </button>
            <button className={styles.btn} onClick={() => { setShowForm(false); setForm({ ...emptyForm }); }}>
              {t('common:cancel')}
            </button>
          </div>
        </div>
      )}

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className={styles.alertsSection}>
          {alerts.map((alert, i) => (
            <div
              key={i}
              className={`${styles.alertCard} ${
                alert.type === 'danger' ? styles.alertDanger :
                alert.type === 'warning' ? styles.alertWarning : styles.alertSuccess
              }`}
            >
              <span className={styles.alertIcon}>
                {alert.type === 'danger' ? '🔴' : alert.type === 'warning' ? '🟡' : '🟢'}
              </span>
              {alert.message}
            </div>
          ))}
        </div>
      )}

      {/* Summary cards */}
      {planned && (
        <div className={styles.summaryRow}>
          {metrics.map((m) => {
            const isPositive = m.key !== 'cost' ? m.variance >= 0 : m.variance <= 0;
            return (
              <div key={m.key} className={styles.summaryCard}>
                <div className={styles.summaryLabel}>{m.label}</div>
                <div className={styles.summaryPlanned}>{t('actuals:plannedLabel')}: {m.format(m.planned)}</div>
                <div className={styles.summaryActual} style={{ color: m.color }}>{m.format(m.actual)}</div>
                {m.actual > 0 && (
                  <div className={`${styles.summaryDelta} ${isPositive ? styles.deltaUnder : styles.deltaOver}`}>
                    {m.variance >= 0 ? '▲' : '▼'} {fmtPct(Math.abs(m.variance), 1)}
                    {' '}({fmtPct(m.completionPct, 0)} complete)
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Variance bars */}
      {planned && metrics.length > 0 && (
        <div className={styles.varianceSection}>
          <div className={styles.sectionTitle}>{t('actuals:varianceAnalysis')}<InfoTip text={t('actuals:tooltips.variance')} /></div>
          {metrics.map((m) => {
            const maxVal = Math.max(m.planned, m.actual, 1);
            const plannedPct = (m.planned / maxVal) * 100;
            const actualPct = (m.actual / maxVal) * 100;
            return (
              <div key={m.key} className={styles.varianceRow}>
                <div className={styles.varianceLabel}>{m.label}</div>
                <div className={styles.varianceBarWrap}>
                  <div className={styles.varianceBarPlanned} style={{ width: `${plannedPct}%` }} />
                  <div
                    className={styles.varianceBarActual}
                    style={{ width: `${actualPct}%`, background: `${m.color}cc` }}
                  />
                </div>
                <div className={styles.varianceValues}>
                  <span style={{ color: 'var(--color-text-muted)' }}>{m.format(m.planned)}</span>
                  <span style={{ color: m.color }}>{m.format(m.actual)}</span>
                </div>
                <div className={`${styles.variancePct} ${m.variance > 0 ? styles.deltaOver : styles.deltaUnder}`}>
                  {m.actual > 0 ? `${m.variance >= 0 ? '+' : ''}${fmtPct(m.variance, 1)}` : '—'}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Actuals timeline */}
      <div className={styles.timelineSection}>
        <div className={styles.sectionTitle}>{t('actuals:recordedActuals', { count: actuals.length })}</div>
        {actuals.length === 0 ? (
          <div className={styles.empty}>{t('actuals:noActualsYet')}</div>
        ) : (
          <div className={styles.timelineGrid}>
            {actuals.map((a) => (
              <div key={a.id} className={styles.timelineCard}>
                <div className={styles.timelinePeriod}>{a.period}</div>
                {a.logements_delivered != null && (
                  <div className={styles.timelineStat}>{fmt(a.logements_delivered)} {t('actuals:log')}</div>
                )}
                {a.cost_spent != null && (
                  <div className={styles.timelineStat}>{fmtCHF(a.cost_spent)}</div>
                )}
                {a.emplois_created != null && (
                  <div className={styles.timelineStat}>{fmt(a.emplois_created)} {t('actuals:emp')}</div>
                )}
                <button
                  className={styles.btn}
                  style={{ marginTop: 6, fontSize: 10, padding: '2px 8px' }}
                  onClick={() => handleDelete(a.id)}
                >
                  {t('common:delete')}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
